import { useMutation, useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { ChangeEvent, ReactElement } from 'react'
import styled from 'styled-components'

import { Column } from '@10xLabs/react-web/components/Column'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { Row } from '@10xLabs/react-web/components/Row'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Select } from 'components/v2/Select'
import { CenteredText } from 'components/Text'
import { Textarea } from 'components/v2/Textarea'
import { CANCEL_LOCAL_TRIP, CANCEL_TRIPS } from 'features/Trips/mutations-legacy'
import { ICancelTripsForm } from 'features/Trips/types'
import { CANCELLATION_CATEGORY_MAP } from 'features/Trips/utils'
import { GTCancellationCategory, GTCancellationSubcategory, GTTripV2, GTTripV2ItineraryStop } from 'gql/types.generated'
import { canBeCancelled, resolvers as tripsResolvers } from 'resolvers/trips'
import { getUnprocessableErrors } from 'utils/error'
import { safeFormatMessage } from 'utils/intl'
import { schema } from './schema'

const DateWrapper = styled.div`
  display: flex;
  margin: 3px 0;
`

const messages = defineMessages({
  categoryPlaceholder: {
    id: 'trips.cancellation.category.placeholder',
    defaultMessage: 'Select a category'
  },
  subcategoryPlaceholder: {
    id: 'trips.cancellation.subcategory.placeholder',
    defaultMessage: 'Select a subcategory'
  },
  commentsPlaceholder: {
    id: 'trips.cancellation.comments.placeholder',
    defaultMessage: 'Add comments'
  }
})

const CANCELLATION_CATEGORIES = [
  GTCancellationCategory.Vehicle,
  GTCancellationCategory.ExternalConditions,
  GTCancellationCategory.Planning,
  GTCancellationCategory.Partner,
  GTCancellationCategory.Driver,
  GTCancellationCategory.BusinessClient,
  GTCancellationCategory.Other
]

const cancellationCategoryMessages = defineMessages({
  [GTCancellationCategory.Vehicle]: {
    id: 'trips.cancellation.category.vehicle',
    defaultMessage: 'Vehicle'
  },
  [GTCancellationCategory.ExternalConditions]: {
    id: 'trips.cancellation.category.externalConditions',
    defaultMessage: 'External conditions'
  },
  [GTCancellationCategory.Planning]: {
    id: 'trips.cancellation.category.planning',
    defaultMessage: 'Planning'
  },
  [GTCancellationCategory.Partner]: {
    id: 'trips.cancellation.category.partner',
    defaultMessage: 'Partner'
  },
  [GTCancellationCategory.Driver]: {
    id: 'trips.cancellation.category.driver',
    defaultMessage: 'Driver'
  },
  [GTCancellationCategory.BusinessClient]: {
    id: 'trips.cancellation.category.businessClient',
    defaultMessage: 'Business client'
  },
  [GTCancellationCategory.Other]: {
    id: 'trips.cancellation.category.other',
    defaultMessage: 'Other'
  }
})

const cancellationSubcategoryMessages = defineMessages({
  [GTCancellationSubcategory.Absence]: {
    id: 'trips.cancellation.subcategory.absence',
    defaultMessage: 'Absence'
  },
  [GTCancellationSubcategory.Accident]: {
    id: 'trips.cancellation.subcategory.accident',
    defaultMessage: 'Accident'
  },
  [GTCancellationSubcategory.MechanicalFailure]: {
    id: 'trips.cancellation.subcategory.mechanicalFailure',
    defaultMessage: 'Mechanical failure'
  },
  [GTCancellationSubcategory.Other]: {
    id: 'trips.cancellation.subcategory.other',
    defaultMessage: 'Other'
  },
  [GTCancellationSubcategory.Roadblock]: {
    id: 'trips.cancellation.subcategory.roadblock',
    defaultMessage: 'Roadblock'
  },
  [GTCancellationSubcategory.ScheduledMaintenance]: {
    id: 'trips.cancellation.subcategory.scheduledMaintenance',
    defaultMessage: 'Scheduled maintenance'
  },
  [GTCancellationSubcategory.Security]: {
    id: 'trips.cancellation.subcategory.security',
    defaultMessage: 'Security'
  },
  [GTCancellationSubcategory.Traffic]: {
    id: 'trips.cancellation.subcategory.traffic',
    defaultMessage: 'Traffic'
  },
  [GTCancellationSubcategory.UnitStoppage]: {
    id: 'trips.cancellation.subcategory.unitStoppage',
    defaultMessage: 'Unit stoppage'
  },
  [GTCancellationSubcategory.Weather]: {
    id: 'trips.cancellation.subcategory.weather',
    defaultMessage: 'Weather'
  },
  [GTCancellationSubcategory.Disability]: {
    id: 'trips.cancellation.subcategory.disability',
    defaultMessage: 'Disability'
  },
  [GTCancellationSubcategory.Protest]: {
    id: 'trips.cancellation.subcategory.protest',
    defaultMessage: 'Protest'
  }
})

interface ICancelTripsModalProps {
  trips: GTTripV2[]
  closeModal: () => void
  onSuccess?: () => void
}

export const CancelTripsModal = ({ trips, closeModal, onSuccess }: ICancelTripsModalProps): ReactElement | null => {
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(tripsResolvers)

  const [cancelLocalTrip] = useMutation(CANCEL_LOCAL_TRIP)
  const [cancelTrips, { loading, error }] = useMutation(CANCEL_TRIPS, {
    onCompleted() {
      trips.forEach((trip) => {
        cancelLocalTrip({
          variables: {
            tripId: trip.id
          }
        })
        closeModal()
        onSuccess?.()
      })
    },
    onError: () => undefined
  })

  const handleSubmit = (values: ICancelTripsForm): void => {
    cancelTrips({
      variables: {
        input: {
          tripIDs: trips.map((trip): TSFixMe => trip.id),
          category: values.category,
          subcategory: values.subcategory || null,
          comments: values.comments || null
        }
      }
    })
  }

  const initialValues: ICancelTripsForm = {
    category: '',
    subcategory: '',
    comments: ''
  }

  const cancellableTrips = trips.filter((trip) => canBeCancelled(trip))

  if (isEmpty(cancellableTrips)) {
    return null
  }

  const isSingleTrip = cancellableTrips.length === 1
  const firstCancellableTrip = cancellableTrips[0]

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          {isSingleTrip ? (
            <FormattedMessage
              id="trip.cancellation.title"
              defaultMessage="Cancel trip {id}"
              values={{
                id: firstCancellableTrip.id.substring(0, 8)
              }}
            />
          ) : (
            <FormattedMessage id="trips.cancellation.title" defaultMessage="Cancel trips" />
          )}
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { handleChange, setValues, handleBlur, touched, errors, values } = props

          const isShowingCancellationSubcategories =
            values.category && !isEmpty(CANCELLATION_CATEGORY_MAP[values.category])

          const handleCancellationCategoryChange = ({ target: { value } }: ChangeEvent<HTMLSelectElement>): void => {
            setValues({
              ...values,
              category: value as GTCancellationCategory | '',
              subcategory: ''
            })
          }

          const firstStop = firstCancellableTrip.itinerary.at(0) as GTTripV2ItineraryStop
          const lastStop = firstCancellableTrip.itinerary.at(-1) as GTTripV2ItineraryStop

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                {isSingleTrip && (
                  <Row>
                    <Column>
                      <div>
                        <FormattedMessage
                          id="trip.cancellation.confirmation.tripDetails"
                          defaultMessage="Trip from <strong>{origin}</strong> to <strong>{destination}</strong>"
                          values={{
                            origin: firstStop.place.name,
                            destination: lastStop.place.name,
                            strong: (msg) => <strong>{msg}</strong>
                          }}
                        />
                      </div>
                      <DateWrapper>
                        <FormattedDate
                          intl={intl}
                          value={firstCancellableTrip.departureAt}
                          month="2-digit"
                          day="2-digit"
                          year="numeric"
                        />
                        &nbsp;
                        <FormattedTimeByDate
                          intl={intl}
                          date={firstCancellableTrip.departureAt}
                          timeZone={firstStop.place.timezone || undefined}
                        />
                      </DateWrapper>
                    </Column>
                  </Row>
                )}
                <Row>
                  <Column>
                    <Select
                      id="category"
                      value={values.category}
                      onChange={handleCancellationCategoryChange}
                      onBlur={handleBlur}
                      touched={touched.category}
                      errors={errors.category}
                      label={
                        <FormattedMessage
                          id="trips.cancellation.form.category.label"
                          defaultMessage="Select a category"
                        />
                      }
                    >
                      <option value="">{intl.formatMessage(messages.categoryPlaceholder)}</option>
                      {CANCELLATION_CATEGORIES.map((category): ReactElement => {
                        return (
                          <option key={category} value={category}>
                            {safeFormatMessage({ intl, messages: cancellationCategoryMessages, key: category })}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                </Row>
                {isShowingCancellationSubcategories && (
                  <Row>
                    <Column>
                      <Select
                        id="subcategory"
                        value={values.subcategory || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched.subcategory}
                        errors={errors.subcategory}
                        label={
                          <FormattedMessage
                            id="trips.cancellation.form.subcategory.label"
                            defaultMessage="Select a subcategory"
                          />
                        }
                      >
                        <option value="">{intl.formatMessage(messages.subcategoryPlaceholder)}</option>
                        {CANCELLATION_CATEGORY_MAP[values.category as GTCancellationCategory].map(
                          (subcategory): ReactElement => {
                            return (
                              <option key={subcategory} value={subcategory}>
                                {safeFormatMessage({
                                  intl,
                                  messages: cancellationSubcategoryMessages,
                                  key: subcategory
                                })}
                              </option>
                            )
                          }
                        )}
                      </Select>
                    </Column>
                  </Row>
                )}
                <Row>
                  <Column>
                    <Textarea
                      id="comments"
                      value={values.comments}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.comments}
                      errors={errors.comments}
                      label={<FormattedMessage id="trips.cancellation.form.comments.label" defaultMessage="Comments" />}
                      placeholder={intl.formatMessage(messages.commentsPlaceholder)}
                    />
                  </Column>
                </Row>
                {isSingleTrip ? (
                  <Row>
                    <Column>
                      <CenteredText>
                        <FormattedMessage
                          id="trip.cancellation.confirmation.text"
                          defaultMessage="Are you sure to cancel this trip?"
                        />
                      </CenteredText>
                    </Column>
                  </Row>
                ) : (
                  <Row>
                    <Column>
                      <CenteredText>
                        <FormattedMessage
                          id="trips.cancellation.confirmation.tripDetails"
                          defaultMessage="Are you sure want to cancel {tripsCount} {tripsCount, plural,
                            one {trip}
                            other {trips}
                          }?"
                          values={{
                            tripsCount: cancellableTrips.length
                          }}
                        />
                      </CenteredText>
                    </Column>
                  </Row>
                )}
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="trips.cancellation.error"
                        defaultMessage="There was an error while cancelling the {tripsCount, plural,
                          one {trip}
                          other {trips}
                        }"
                        values={{
                          tripsCount: cancellableTrips.length
                        }}
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="modal.actions.close" defaultMessage="No, close" />
                  </Button>
                  <Button kind="primary" loading={loading}>
                    <FormattedMessage
                      id="trips.cancellation.action"
                      defaultMessage="Cancel {tripsCount, plural,
                        one {trip}
                        other {trips}
                      }"
                      values={{
                        tripsCount: cancellableTrips.length
                      }}
                    />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
